import { useQuery } from "@tanstack/react-query";
import React, { useEffect, useMemo, useRef } from "react";
import { ThreeDots } from "react-loader-spinner";
import { Link } from "react-router-dom";

import RenderWhen from "../../../components/RenderWhen";
import { getClassificationByValue } from "../../../lib/enums/ThreatStatusEnum";
import api from "../../../services/api";
import { settings } from "../../../shim/jsbridge";
import { SECTIONS } from "../../samples/consts";
import {
  useGetInterestingStrings,
  useGetMediaAddress
} from "../../samples/hooks";
import { pathToSS } from "../../samples/util";
import { Classification, Rha1Counters, SimpleGraphProps } from "../@types";
import { reportSummaryNodePos } from "../graph-util";
import useRenderDroppedFilesRS from "../hooks/render/useRenderDroppedFilesRS";
import useRenderDynamicNetworkReferencesRS from "../hooks/render/useRenderDynamicNetworkReferencesRS";
import useRenderExtractedFilesRS from "../hooks/render/useRenderExtractedFilesRS";
import useRenderNetworkConnectionsRS from "../hooks/render/useRenderNetworkConnectionsRS";
import useRenderNetworkReferencesRS from "../hooks/render/useRenderNetworkReferencesRS";
import useRenderParentsRS from "../hooks/render/useRenderParentsRS";
import useRenderRha1SimilarityRS from "../hooks/render/useRenderRha1SimilarityRS";
import useInitExtractedFilesData from "../hooks/useInitExtractedFilesData";
import useInitNetworkConnectionsData from "../hooks/useInitNetworkConnectionsData";
import useInitNetworkReferences from "../hooks/useInitNetworkReferences";
import useInitParentsData from "../hooks/useInitParentsData";
import useInitRlSandboxData from "../hooks/useInitRlSandboxData";
import useVisNetwork from "../hooks/useVisNetwork";
import { getFileIcon } from "../utils";

const GRAPH_PATH = "/graph/new";

const RelationshipGraphRS: React.FC<SimpleGraphProps> = ({
  sampleDetails: {
    sha1: hash,
    relationship_id: relationshipId,
    sample_source_id: sourceId,
    sample_file_type: sampleFileType,
    sample_file_subtype: sampleFileSubType,
    sample_threat_status: sampleThreatStatus,
    sample_identification: sampleIdentification,
    sample_source_sha1: sampleSourceSha1,
    extracted_files_counts: sampleExtractedFilesCount
  },
  sandboxNetworkData,
  isLoadingSandboxNetworkData = false,
  networkOptions
}) => {
  const canvasRef = useRef<HTMLDivElement>(null);
  const {
    graphActions: { renderControlNode },
    network
  } = useVisNetwork({
    initEdges: [],
    initNodes: [
      {
        id: "rootNode",
        title: hash,
        shape: "image",
        fixed: true,
        size: 60,
        ...getFileIcon(
          sampleFileType,
          sampleFileSubType,
          getClassificationByValue(sampleThreatStatus) as Classification,
          sampleIdentification
        ),
        ...reportSummaryNodePos.rootNode
      }
    ],
    networkOptions,
    canvasRef
  });

  const { data: interestingStringsData } = useGetInterestingStrings(
    hash,
    Number(sourceId),
    Number(relationshipId)
  );

  const { data: mediaAddress, isLoading: isLoadingMediaAddresses } =
    useGetMediaAddress(hash);

  const {
    data: { relationshipsData },
    counts: { totalParents },
    isFetching: isFetchingParents
  } = useInitParentsData(hash);

  const {
    data: { sandboxNetworkDataMerged },
    counts: { totalNetworkConnections },
    isFetching: isFetchingNetworkConnections
  } = useInitNetworkConnectionsData(sandboxNetworkData);

  const strings = useMemo(() => {
    const interestingStrings = Object.keys(
      interestingStringsData?.strings ?? {}
    );
    const mediaAddressUrls = (mediaAddress?.results ?? []).map(
      (item: { value: string }) => item.value
    );
    return [...new Set([...interestingStrings, ...mediaAddressUrls])];
  }, [interestingStringsData?.strings, mediaAddress?.results]);

  const {
    data: { networkRepData },
    counts: { totalNetworkReferences },
    isFetching: isFetchingNetworkReferences
  } = useInitNetworkReferences(strings, strings?.length);

  const {
    data: { dynamicNetworkRepData },
    counts: {
      pieCountsDroppedFiles,
      totalDynamicNetworkConnections,
      totalDroppedFiles
    },
    isFetching: isFetchingRlSandbox
  } = useInitRlSandboxData(hash);

  const {
    counts: { extractedFilesTotalCount },
    isFetching: isFetchingExtractedFiles
  } = useInitExtractedFilesData(
    sampleSourceSha1,
    `${relationshipId}`,
    sampleExtractedFilesCount
  );

  const { data: rhaCounters, isFetching: isFetchingRhaCounters } = useQuery({
    queryKey: ["rha1_counters", hash],
    queryFn: () => api.rha1Counters(hash),
    enabled: Boolean(hash),
    select: ({ data }: { data: Rha1Counters }) => data
  });

  useRenderNetworkConnectionsRS({
    data: sandboxNetworkDataMerged,
    renderer: renderControlNode,
    counts: totalNetworkConnections
  });

  useRenderNetworkReferencesRS({
    data: networkRepData,
    renderer: renderControlNode,
    counts: totalNetworkReferences
  });

  useRenderDynamicNetworkReferencesRS({
    data: dynamicNetworkRepData,
    renderer: renderControlNode,
    counts: totalDynamicNetworkConnections
  });

  useRenderDroppedFilesRS({
    renderer: renderControlNode,
    counts: { pie: pieCountsDroppedFiles, total: totalDroppedFiles }
  });

  useRenderExtractedFilesRS({
    renderer: renderControlNode,
    counts: {
      pie: sampleExtractedFilesCount,
      total: extractedFilesTotalCount
    }
  });

  useRenderParentsRS({
    data: relationshipsData,
    renderer: renderControlNode,
    counts: totalParents
  });

  useRenderRha1SimilarityRS({
    renderer: renderControlNode,
    counts: {
      pie: rhaCounters
    }
  });
  const isFetching =
    isFetchingParents ||
    isFetchingExtractedFiles ||
    isFetchingRlSandbox ||
    isFetchingNetworkReferences ||
    isFetchingNetworkConnections ||
    isLoadingSandboxNetworkData ||
    isFetchingRhaCounters ||
    isLoadingMediaAddresses;

  useEffect(() => {
    // fit nodes on window resize
    const handleResize = () => {
      network?.fit({ maxZoomLevel: 0.35 });
    };
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [network]);

  const graphExplorerPath = hash ? `${GRAPH_PATH}?hash=${hash}` : GRAPH_PATH;

  return (
    <Link
      to={
        settings.FEATURE_GRAPH_EXPLORER
          ? graphExplorerPath
          : pathToSS(
              hash,
              sourceId,
              SECTIONS.FILE_ANALYSIS.RELATIONSHIPS_GRAPH,
              relationshipId
            )
      }
      state={{ hash }}
      data-testid="report_summary_relationship_graph_main_link"
    >
      <div className="rl-graph-container__main">
        <RenderWhen condition={isFetching}>
          <ThreeDots
            wrapperClass="summary-header__analyzing-spinner"
            color="#000"
            height={20}
            width={20}
          />
        </RenderWhen>
        <div style={{ height: 250 }} ref={canvasRef} />
      </div>
    </Link>
  );
};
export default RelationshipGraphRS;
